// Guardrail protocol shapes — moved verbatim from `src/types/guardrails.ts`
// (which now re-exports them name-intact) with the `calls/agents` C slice
// (ARCFE-1097).
//
// WHY THEY ARE CONTRACTS AND NOT UI TYPES. Every name here is a wire shape or
// a wire constant: the two string unions enumerate the categories and fields
// the ml-scribe guardrail service returns, the two interfaces are its v2
// response body, and `GUARDRAILS_API_VERSION_HEADER` is the header name the
// api layer sends. Nothing renders. The LABELS that turn a category into
// English (`GUARDRAIL_CATEGORY_LABELS`, `GUARDRAIL_FIELD_LABELS`) deliberately
// did NOT move — they are copy, and copy stays app-side (see
// `src/utils/guardrail-utils.ts`, whose split this file is half of).
//
// `GuardrailField` and `GuardrailCategory` are carried across even though the
// api layer never reads them: `useSkillGuardrail` and `guardrail-inline-block`
// narrow on them, so leaving them behind would split one protocol enumeration
// across two homes.

/** Category bucket the guardrail service assigns to a violation. */
export type GuardrailCategory =
  | 'prompt_injection'
  | 'harmful_content'
  | 'medical_safety'
  | 'pci_violation';

/** Agent-config field a guardrail check can be run against. */
export type GuardrailField =
  | 'system_instruction'
  | 'knowledge_base'
  | 'appointment_rules'
  | 'escalation_rules'
  | 'greeting_text'
  | 'after_hours_greeting_text'
  | 'summary_template'
  | 'sms_summary_template'
  | 'objective';

/**
 * One violation in a v2 guardrail response.
 *
 * `field` and `category` are typed `string`, not the unions above, and that is
 * transcribed rather than tightened: the service may return a category the FE
 * does not know yet, which is exactly why `isGuardrailCategory` exists.
 */
export interface GuardrailViolationV2 {
  field: string;
  category: string;
  triggered_text: string;
  user_message: string;
}

/** Body of `POST /calls/agents/v2/guardrails/check`. */
export interface GuardrailCheckResponseV2 {
  passed: boolean;
  violations: GuardrailViolationV2[];
  checked_at?: string | null;
}

/**
 * Request header that opts a call into the v2 guardrail service.
 *
 * A stable identifier, so it belongs at this door. The DECISION to send it is
 * app-resolved — it hangs off `IS_COMMS_GUARDRAILS_V2_ENABLED`, which the
 * package never learns the name of (ARCFE-1060: the flag crosses as a getter).
 */
export const GUARDRAILS_API_VERSION_HEADER = 'X-Guardrails-API-Version';

/**
 * The violation shape the calls UI and hooks pass around.
 *
 * An alias, not a second declaration: `src/utils/guardrail-utils.ts` exported
 * `GuardrailViolation = GuardrailViolationV2` and both names are load-bearing
 * across ~10 call sites, so both travel.
 */
export type GuardrailViolation = GuardrailViolationV2;
